import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { CreditCard, Lock } from 'lucide-react';
import { API_URL } from '../../config';

// ═══════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL - WRAPPER STRIPE
// ═══════════════════════════════════════════════════════════════

export default function StripePaymentForm({ cart, getTotalAmount, onSuccess, onCancel }) {
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState('');
  const [orderId, setOrderId] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStripeConfig();
  }, []);

  const loadStripeConfig = async () => {
    try {
      // Récupérer la publishable key depuis le backend
      const response = await fetch(`${API_URL}/api/stripe/config`);
      const data = await response.json();
      
      const stripe = await loadStripe(data.publishable_key);
      setStripePromise(stripe);
      
      // Créer le PaymentIntent
      await createPaymentIntent();
    } catch (error) {
      console.error('❌ Erreur Stripe config:', error);
      alert('❌ Erreur de chargement Stripe');
      setLoading(false);
    }
  };

  const createPaymentIntent = async () => {
    try {
      const firstItem = cart[0];
      const totalAmount = getTotalAmount();

      // ✅ Déterminer si c'est un abonnement ou des crédits
      const isSubscription = firstItem.service && typeof firstItem.service === 'object';
      const isCredits = firstItem.service && typeof firstItem.service === 'string';

      let payload;

      if (isCredits) {
        // ✅ ACHAT DE CRÉDITS
        payload = {
          user_id: localStorage.getItem('user_id') || 'user_test',
          service_id: firstItem.service, // C'est déjà un string ('video_ads', 'teckia_vtryon', etc.)
          amount: totalAmount,
          plan: 'credits',
          duration: 0,
          purchase_type: 'credits',
          items: JSON.stringify(cart) // Convertir en JSON pour les metadata
        };
      } else {
        // ✅ ABONNEMENT CLASSIQUE
        payload = {
          user_id: localStorage.getItem('user_id') || 'user_test',
          service_id: firstItem.service.id,
          amount: totalAmount,
          plan: firstItem.service.id,
          duration: 1,
          config: firstItem.config
        };
      }

      console.log('💳 Creating Stripe PaymentIntent:', payload);

      const response = await fetch(`${API_URL}/api/stripe/create-payment-intent`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (result.success) {
        setClientSecret(result.client_secret);
        setOrderId(result.order_id);
        setLoading(false);
      } else {
        alert('❌ Erreur: ' + result.error);
        setLoading(false);
      }
    } catch (error) {
      console.error('❌ Erreur PaymentIntent:', error);
      alert('❌ Erreur réseau');
      setLoading(false);
    }
  };

  const appearance = {
    theme: 'night',
    variables: {
      colorPrimary: '#a855f7',
      colorBackground: '#1e1b4b',
      colorText: '#ffffff',
      colorDanger: '#ef4444',
      fontFamily: 'system-ui, sans-serif',
      spacingUnit: '4px',
      borderRadius: '12px'
    }
  };

  const options = {
    clientSecret,
    appearance
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-r from-purple-900/20 to-blue-900/20 backdrop-blur-xl rounded-2xl p-6 border border-purple-500/30">
        <div className="flex items-center justify-center gap-3 text-white">
          <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          <span>Chargement du formulaire de paiement...</span>
        </div>
      </div>
    );
  }

  if (!clientSecret || !stripePromise) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-6">
        <p className="text-red-300">❌ Impossible de charger le formulaire de paiement</p>
        <button
          onClick={onCancel}
          className="mt-4 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-xl text-white transition-all"
        >
          Retour
        </button>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise} options={options}>
      <CheckoutForm 
        orderId={orderId}
        amount={getTotalAmount()}
        onSuccess={onSuccess}
        onCancel={onCancel}
      />
    </Elements>
  );
}

// ═══════════════════════════════════════════════════════════════
// COMPOSANT FORMULAIRE DE PAIEMENT
// ═══════════════════════════════════════════════════════════════

function CheckoutForm({ orderId, amount, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setErrorMessage('');

    try {
      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/payment/success`,
        },
        redirect: 'if_required'
      });

      if (error) {
        setErrorMessage(error.message);
        setProcessing(false);
      } else {
        // Paiement réussi
        console.log('✅ Paiement Stripe réussi');
        onSuccess();
      }
    } catch (err) {
      console.error('❌ Erreur:', err);
      setErrorMessage('Une erreur est survenue. Réessayez.');
      setProcessing(false);
    }
  };

  return (
    <div className="bg-gradient-to-r from-purple-900/20 to-blue-900/20 backdrop-blur-xl rounded-2xl p-6 border border-purple-500/30">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
          <CreditCard className="w-6 h-6 text-white" />
        </div>
        <div>
          <h4 className="text-white font-bold text-lg">Paiement par carte</h4>
          <p className="text-white/60 text-sm">Sécurisé par Stripe</p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="mb-6">
          <PaymentElement />
        </div>

        {errorMessage && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-300 text-sm">
            {errorMessage}
          </div>
        )}

        <div className="bg-white/5 rounded-xl p-4 mb-6 border border-white/10">
          <div className="flex justify-between text-white mb-2">
            <span>Commande:</span>
            <span className="font-mono">#{orderId}</span>
          </div>
          <div className="flex justify-between text-white font-bold text-xl pt-2 border-t border-white/10">
            <span>Total:</span>
            <span>{amount.toLocaleString()} Ar</span>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={processing}
            className="flex-1 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={!stripe || processing}
            className="flex-1 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {processing ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Traitement...
              </>
            ) : (
              <>
                <Lock className="w-5 h-5" />
                Payer {amount.toLocaleString()} Ar
              </>
            )}
          </button>
        </div>
      </form>

      <div className="mt-4 flex items-center justify-center gap-2 text-xs text-white/40">
        <Lock className="w-3 h-3" />
        <span>Paiement 100% sécurisé SSL • Powered by Stripe</span>
      </div>
    </div>
  );
}